import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../lib/auth';
import { userAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { User, Mail } from 'lucide-react';

export const ProfileUpdateDialog = ({ open, onOpenChange, userId }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const { user, refreshUser } = useAuthStore();

  // Initialize with current user data when dialog opens
  useEffect(() => {
    if (open && user) {
      setName(user.name || '');
      setEmail(user.email || '');
    }
  }, [open, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!name.trim()) {
      toast.error('Please enter your name');
      return;
    }

    if (!email.trim()) {
      toast.error('Please enter your email');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      const targetUserId = userId || user?.id;
      if (!targetUserId) {
        toast.error('User ID not found');
        return;
      }

      await userAPI.updateProfile(targetUserId, name, email);
      toast.success('Profile updated successfully');
      
      onOpenChange(false);
      
      // Refresh user
      await refreshUser();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update profile';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#1A1A1A] border border-[#3F3F46] text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-[#FF6600]" />
            Update Profile
          </DialogTitle>
          <DialogDescription className="text-[#A1A1AA]">
            Update your profile information
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-200">
              Full Name
            </label>
            <Input
              type="text"
              placeholder="Enter your full name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={loading}
              className="bg-[#2A2A2A] border-[#3F3F46] text-white placeholder-[#52525B]"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-200">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                className="bg-[#2A2A2A] border-[#3F3F46] text-white placeholder-[#52525B] pl-10"
              />
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 justify-end pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
              className="border-[#3F3F46] text-[#A1A1AA] hover:bg-[#1E1E1E]"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-[#FF6600] hover:bg-[#E55D00] text-white"
            >
              {loading ? 'Updating...' : 'Update Profile'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileUpdateDialog;
